import moment from 'moment-timezone';
import Commons from './commons';
import { SRV_BASE_URL } from './constants';

const ATHENS_TIMEZONE = "Europe/Athens";

// ---------------------------------------------------------------------------
// Contact emails
// ---------------------------------------------------------------------------

export function buildContactEmails(sale, contacts, company, sendPaymentLink, t) {
    return contacts.map(contact => {
        const email = contact.email ?? contact.value;
        const name = contact.name ?? contact.value;
        const subject = t("emailSubject", { company: company.name });
        let body = "<p>" + t("emailBody1", { contactName: name }) + "</p>";
        body += "<p>" + t("emailBody2", { company: company.name }) + "</p>";
        const link = `<a href='https://app.timologic.gr/mydata/${sale.invoiceUid}'>${sale.invoiceType.name} ${sale.series}${sale.invoiceNumber}</a>`;
        body += "<p>" + t("emailBody3", { link }) + "</p>";
        if (sendPaymentLink && company.vivaQuickPay) {
            body += "<p>" + t("emailVivaPayMess") + "</p>";
            body += `<p class='viva-pay-btn-cont'><a class='viva-pay-btn' href='${company.vivaQuickPay}/${sale.totalPayment}'>${t("payWithViva")}</a></p>`;
        }
        if (sendPaymentLink && company.stripeAccountID) {
            body += "<p>" + t("emailStripePayMess") + "</p>";
            body += `<p class='stripe-pay-btn-cont'><a class='stripe-pay-btn' href='${SRV_BASE_URL}/api/stripe/checkout/${company.id}?amount=${sale.totalWithVat}&saleInstanceId=${sale.id}'>${t("payWithTimologicPay")}</a></p>`;
        }
        return { subject, body, email };
    });
}

// ---------------------------------------------------------------------------
// Sale payload builder  (was: prepareSale)
// ---------------------------------------------------------------------------

export function buildSalePayload(formSale, {
    entries,
    moreTaxes,
    mark,
    saleId,
    isCredit,
    suggestedInClassCatsOfInvType,
    totals,
    company,
    userEmail,
    contacts,
    sendPaymentLink,
    t,
}) {
    const sale = JSON.parse(JSON.stringify(formSale));

    // — date formatting —
    {
        const fmt = "YYYY-MM-DD HH:mm";
        const athensTime = moment.tz(sale.invoiceDate, ATHENS_TIMEZONE);
        const iso = athensTime.toISOString();
        const [datePart, timePartWithMs] = iso.split("T");
        const [year, month, day] = datePart.split("-");
        const [timePart] = timePartWithMs.split(".");
        sale.invoiceDate = `${day}/${month}/${year} ${timePart}+00:00`;
    }
    if (sale.dispatchDate) {
        // After JSON.parse(JSON.stringify()), Date objects become ISO strings.
        // Preserve the wall-clock time the user picked, expressed in Athens timezone.
        const fmt = "YYYY-MM-DD HH:mm";
        const localWallClock = moment(sale.dispatchDate).format(fmt);
        const athensTime = moment.tz(localWallClock, fmt, ATHENS_TIMEZONE);
        const iso = athensTime.toISOString();
        const [datePart, timePartWithMs] = iso.split("T");
        const [year, month, day] = datePart.split("-");
        const [timePart] = timePartWithMs.split(".");
        sale.dispatchDate = `${day}/${month}/${year} ${timePart}+00:00`;
    }
    // — mark, transmission, id —
    if (mark && !isCredit) sale.mark = mark;
    // sale.transmissionType = sale.sendAsPendingPayment ? 4 : 5;
    sale.transmissionType = 5;

    if (saleId && !isCredit) sale.id = saleId;

    // — entries —
    sale.saleEntries = entries.map(e =>
        buildSaleEntry(e, sale, isCredit, suggestedInClassCatsOfInvType, formSale.selfPricing, saleId)
    );

    // — misc —
    sale.movePurpose = sale.movePurpose ? sale.movePurpose.value : null;
    sale.templateLang = sale.templateLang ? sale.templateLang.toUpperCase() : "GR";

    // — branch —
    sale.issuerPhone = sale.branch.model?.phone ?? null;
    sale.issuerEmail = sale.branch.model?.email ?? null;
    sale.issuerSubjectField = sale.branch.model?.subjectField ?? null;
    sale.branchId = sale.branch.value;
    sale.branchAddress = sale.branch.model.address;
    sale.branchNumber = sale.branch.model.branchNumber;
    sale.branchName = sale.branch.model.name;
    sale.branch = {
        id: sale.branch.value,
        name: sale.branch.label,
        address: sale.branch.model.address,
        subjectField: sale.branch.model.subjectField,
        branchNumber: sale.branch.model.branchNumber,
        logo: sale.branch.model.logo,
    };

    // — company —
    sale.company = { id: company.id, taxpayerId: company.taxpayerId, name: company.name };

    // — customer —
    const custTaxOffice = sale.taxOffice && typeof sale.taxOffice === 'object' ? { name: sale.taxOffice.label, id: sale.taxOffice.value } : null;
    sale.taxOffice = custTaxOffice;
    if (sale.customer) {
        sale.customerName = sale.customer.label;
        sale.customerTaxPayerId = sale.taxpayerId;
        sale.customerAddress = sale.fulladdress;
        sale.customerCountry = sale.country?.value;
        sale.customerAddressNumber = sale.addressNumber;
        sale.customerPostalCode = sale.postalCode;
        sale.customerCity = sale.city;
        sale.taxOfficeName = custTaxOffice ? custTaxOffice.name : null;
    }

    // — correlative / connected —
    if (sale.correlativeSales != null) {
        sale.correlativeSaleInstances = [{ id: sale.correlativeSales.value }];
    }
    if (sale.connectedSales != null) {
        sale.connectedSaleInstances = sale.connectedSales.map(inst => ({ id: inst.value }));
    }

    // — invoice type (replaces the rich object with the DTO shape) —
    sale.myDataInvoiceType = sale.invoiceType.myDataInvoiceType;
    sale.invoiceTypeName = sale.invoiceType.label;
    sale.invoiceTypeId = sale.invoiceType.value;
    sale.invoiceType = {
        name: sale.invoiceType.label,
        id: sale.invoiceType.value,
        myDataInvoiceType: sale.invoiceType.myDataInvoiceType,
    };

    sale.series = sale.series.label;

    // — payment methods —
    sale.paymentMethods = buildPaymentMethods(sale);

    sale.currency = sale.currency ? sale.currency.value : null;

    // — totals —
    Object.assign(sale, {
        totalWithVat: totals.totalWithVat,
        totalWithoutVat: totals.totalWithoutVat,
        totalWithDiscount: totals.totalWithDiscount,
        totalWithoutDiscount: totals.totalWithoutDiscount,
        totalPayment: totals.totalPayment,
        totalDiscount: totals.totalDiscount,
        totalOtherTaxes: totals.totalOtherTaxes,
        totalFeesTaxes: totals.totalFeesTaxes,
        totalStumpOfDutyTaxes: totals.totalStumpOfDutyTaxes,
        totalWithHoldTaxes: totals.totalWithHoldTaxes,
        totalDeductionsTaxes: totals.totalDeductionsTaxes,
    });

    // — contact emails —
    const isStoredInvoice = ["invoice1_1", "invoice2_1"].includes(sale.myDataInvoiceType);
    sale.contactEmails = {
        contacts: buildContactEmails(sale, contacts, company, sendPaymentLink, t),
        store: isStoredInvoice,
        taxpayerId: company.taxpayerId,
        fromEmail: userEmail,
    };

    // — more taxes —
    if (sale.myDataInvoiceType === "invoice8_2") {
        sale.saleInstanceMoreTaxes = [{ amount: sale.otherTaxAmount, myDataMoreTax: "TAX_" + sale.otherTaxCategory.uid }];
    } else {
        sale.saleInstanceMoreTaxes = moreTaxes.map(tax => {
            let taxVal = tax.myDataMoreTax.value;
            if (!taxVal.toString().includes("TAX_")) taxVal = "TAX_" + taxVal;
            return {
                id: !isCredit && sale.myDataInvoiceType !== "invoice5_1" ? tax.id : null,
                amount: tax.amount,
                myDataMoreTax: tax.myDataMoreTax.type === "DEDUCTION" ? null : taxVal,
                deduction: tax.myDataMoreTax.type === "DEDUCTION"
                    ? { id: tax.myDataMoreTax.uid, name: tax.myDataMoreTax.label, amountValue: tax.amount, subtracted: tax.subtracted ?? tax.myDataMoreTax.subtracted }
                    : null,
                type: tax.myDataMoreTax.type,
            };
        });
    }

    // — cleanup —
    delete sale.taxpayerId;
    delete sale.city;
    delete sale.country;
    delete sale.postalCode;
    delete sale.fulladdress;
    delete sale.addressNumber;
    delete sale.customerContacts;
    delete sale.otherTaxAmount;
    delete sale.otherTaxCategory;

    return sale;
}

function buildSaleEntry(e, sale, isCredit, suggestedInClassCatsOfInvType, selfPricing, saleId) {
    const saleEntry = {};

    if (saleId && !isCredit || (!isCredit && sale.invoiceType.myDataInvoiceType !== "invoice5_1" && sale.invoiceType.myDataInvoiceType !== "invoice9_1" && sale.invoiceType.myDataInvoiceType !== "invoice11_4")) {
        saleEntry.id = e.id;
    }

    saleEntry.product = Number.isInteger(e.product.value)
        ? { id: e.product.value, name: e.product.label }
        : { id: undefined, name: e.product.label };

    saleEntry.measurementUnit = e.measurementUnit.value === "-" ? "PIECES" : e.measurementUnit.value;
    saleEntry.quantity = e.quantity === "-" ? 1 : e.quantity;

    if (e.vat.value == "VAT_NEW") {
        saleEntry.vatAmount = e.vat.model.value;
    }


    saleEntry.vat = e.vat.value;
    saleEntry.discount = e.discount;
    saleEntry.unitPrice = e.unitPrice;
    saleEntry.priceWithVat = e.priceWithVat;
    saleEntry.priceWithoutVat = e.priceWithoutVat;

    if (e.vatCategoryException) {
        saleEntry.vatCategoryException = e.vatCategoryException.value;
    }

    saleEntry.saleEntryClassifications = buildEntryClassifications(e, sale, selfPricing, suggestedInClassCatsOfInvType);
    saleEntry.saleEntryMoreTaxes = buildEntryMoreTaxes(e);

    return saleEntry;
}

function buildEntryClassifications(e, sale, selfPricing, suggestedInClassCatsOfInvType) {
    const mdit = sale.invoiceType.myDataInvoiceType;
    const asExpenses = selfPricing || Commons.isOnGroup(mdit, "isExpense");
    const q = e.quantity === "-" ? 1 : e.quantity;

    if (e.extraLineFields != null) {
        return e.extraLineFields.map(f => ({
            classificationCategory: f.classificationCategory.value,
            classificationValue: f.classificationValue.value,
            amount: parseFloat(f.amount).toFixed(2),
        }));
    }

    const prodClass = e.product?.model
        ? getProductClassificationOfInvoiceType(e.product.model.productClassifications, mdit)
        : null;

    if (prodClass != null) {
        const amnt = prodClass.percent ? e.priceWithoutVat * prodClass.amount / 100 : 0;
        return [{
            classificationCategory: prodClass.classificationCategory,
            classificationValue: prodClass.classificationValue,
            amount: parseFloat(amnt).toFixed(2),
        }];
    }

    if (sale.invoiceType.invoiceTypeClassifications?.length > 0) {
        return sale.invoiceType.invoiceTypeClassifications
            .filter(_class => asExpenses === _class.selfPricing)
            .map(_class => {
                let _amount = _class.percent ? e.unitPrice * q * _class.amount / 100 : _class.amount;
                _amount -= e.discount * _amount / 100;
                return {
                    classificationCategory: _class.classificationCategory,
                    classificationValue: _class.classificationValue,
                    amount: parseFloat(_amount).toFixed(2),
                };
            });
    }

    if (suggestedInClassCatsOfInvType?.length > 0) {
        let _amount = e.unitPrice * q;
        _amount -= e.discount * _amount / 100;
        return [{
            classificationCategory: suggestedInClassCatsOfInvType[0]?.value ?? null,
            classificationValue: suggestedInClassCatsOfInvType[0]?.classificationValues[0] ?? null,
            amount: _amount,
        }];
    }

    return [];
}

function buildEntryMoreTaxes(e) {
    if (!e.taxes) return [];
    return e.taxes.map(tax => {
        let taxVal = tax.myDataMoreTax.value;
        if (!taxVal.toString().includes("TAX_")) taxVal = "TAX_" + taxVal;
        return {
            amount: tax.amount,
            myDataMoreTax: tax.myDataMoreTax.type === "DEDUCTION" ? null : taxVal,
            deductionName: tax.myDataMoreTax.label,
            deductionIsSubtracted: tax.subtracted,
            deductionAmountValue: tax.amount,
            type: tax.myDataMoreTax.type,
        };
    });
}

function buildPaymentMethods(sale) {
    const methods = [];
    const paymentType = sale.paymentType?.value;
    const paymentType2 = sale.paymentType2?.value;

    if (paymentType) {
        methods.push({
            paymentType,
            paymentAmount: sale.paymentAmount,
            preloadTransaction: sale.preloadTransaction,
            preloadExpiration: sale.preloadExpiration,
            transactionId: sale.paymentType.model?.transactionId ?? null,
            signature: sale.paymentType.model?.signature ?? null,
            uid: sale.paymentType.model?.uid ?? null,
            signatureClearText: sale.paymentType.model?.signatureClearText ?? null,
        });
    }
    if (paymentType2) {
        methods.push({
            paymentType: paymentType2,
            paymentAmount: sale.paymentAmount2,
            transactionId: sale.paymentType2.model?.transactionId ?? null,
            signature: sale.paymentType2.model?.signature ?? null,
            uid: sale.paymentType2.model?.uid ?? null,
            preloadTransaction: sale.preloadTransaction2,
            preloadExpiration: sale.preloadExpiration2,
            signatureClearText: sale.paymentType.model?.signatureClearText ?? null,
        });
    }
    return methods;
}

function getProductClassificationOfInvoiceType(classifications, invtype) {
    if (!classifications) return null;
    return classifications.find(c => c.invoiceType.myDataInvoiceType === invtype) ?? null;
}

// ---------------------------------------------------------------------------
// Loaded sale transformer  (was: setDataWithClassifications)
// Returns { entries, formUpdates } — formUpdates is an array of [field, value] pairs
// ---------------------------------------------------------------------------

export function transformLoadedSaleEntries(saleObject, optionIncClassCategories, myDataMoreTaxesOptions, {
    isPreSale = false,
    selfPricing,
    invoiceType,
    currentInvoiceTypeMyData,
    t,
}) {
    const formUpdates = [];

    const entries = saleObject.saleEntries.map(el => {
        const product = isPreSale
            ? { label: el.product.label, value: el.product.value, model: el.product.model }
            : { label: el.product.name, value: el.product.name, model: el.product };

        let vat;
        if (isPreSale) {
            vat = { label: el.vat.label, value: el.vat.value, model: el.vat.model ?? el.vat };
        } else {
            const _lab = el.vat.id.replace("VAT_", "");
            vat = { label: _lab === "EMPTY" ? "-" : _lab, value: el.vat.id, model: el.vat };
        }

        const mu = isPreSale
            ? { value: el.measurementUnit.value, label: t(el.measurementUnit.label) }
            : { value: el.measurementUnit, label: t(el.measurementUnit) };

        const extraLineFields = buildLoadedClassifications(
            el, optionIncClassCategories, isPreSale, selfPricing, invoiceType, t
        );

        const taxes = buildLoadedMoreTaxes(el, myDataMoreTaxesOptions, t);

        if (currentInvoiceTypeMyData === "invoice8_2" && taxes[0]) {
            formUpdates.push(
                ["otherTaxCategory", taxes[0].myDataMoreTax],
                ["otherTaxAmount", taxes[0].amount]
            );
        }

        const _vatDisplay = vat.model?.value ?? vat.label;
        const vat_val = _vatDisplay === "-" ? 0 : _vatDisplay;
        const _vatIsAmount = vat.model?.isAmount === true;
        const _qty = el.quantity === "-" ? 1 : parseFloat(el.quantity);
        const unitPriceWithVat = _vatIsAmount
            ? el.unitPrice + parseFloat(vat_val) / _qty
            : el.unitPrice * (1 + vat_val / 100);
        const vatCategoryException = el.vatCategoryException
            ? { label: t(el.vatCategoryException), value: el.vatCategoryException }
            : null;

        return {
            product,
            unitPrice: parseFloat(el.unitPrice).toFixed(2),
            unitPriceWithVat: parseFloat(unitPriceWithVat).toFixed(4),
            quantity: el.quantity,
            id: el.id,
            measurementUnit: mu,
            vat,
            discount: el.discount ?? "0.00",
            priceWithoutVat: parseFloat(el.priceWithoutVat.toString()).toFixed(2),
            priceWithVat: el.priceWithVat,
            extraLineFields,
            vatCategoryException,
            taxes,
        };
    });

    return { entries, formUpdates };
}

function buildLoadedClassifications(el, optionIncClassCategories, isPreSale, selfPricing, invoiceType, t) {
    if (el.saleEntryClassifications?.length > 0) {
        return el.saleEntryClassifications
            .filter(e => e.classificationCategory)
            .map(e => {
                const classcat = e.classificationCategory;
                const prefix = classcat.replace("CATEGORY_", "") + ") ";
                const _cls = optionIncClassCategories[classcat] || [];
                const newcls = _cls.map(c => ({ label: t(c), value: c }));
                return {
                    classificationCategory: { value: classcat, label: prefix + t(classcat) },
                    classificationValue: {
                        value: e.classificationValue,
                        label: t(e.classificationValue) + " (" + e.classificationValue + ")",
                    },
                    classificationValues: newcls,
                    amount: parseFloat(e.amount).toFixed(2),
                };
            });
    }

    if (!isPreSale) return [];

    const asExpenses = selfPricing || Commons.isOnGroup(invoiceType?.myDataInvoiceType, "isExpense");
    const typeClassifications = invoiceType?.invoiceTypeClassifications;

    if (typeClassifications?.length > 0) {
        const q = el.quantity === "-" ? 1 : el.quantity;
        return typeClassifications
            .filter(_class => asExpenses === _class.selfPricing)
            .map(_class => {
                let _amount = _class.percent ? el.unitPrice * q * _class.amount / 100 : _class.amount;
                _amount -= el.discount * _amount / 100;
                const prefix = _class.classificationCategory.replace("CATEGORY_", "") + ") ";
                return {
                    classificationCategory: {
                        label: prefix + t(_class.classificationCategory),
                        value: _class.classificationCategory,
                    },
                    classificationValue: {
                        label: t(_class.classificationValue) + " (" + _class.classificationValue + ")",
                        value: _class.classificationValue,
                    },
                    classificationValues: _class.classificationValues,
                    amount: parseFloat(_amount).toFixed(2),
                };
            });
    }

    if (optionIncClassCategories[0]) {
        const _cls = optionIncClassCategories[0];
        const opts = (_cls.classificationValues || []).map(element => ({
            value: element,
            label: t(element) + " (" + element + ")",
        }));
        return [{
            classificationCategory: { value: _cls.value, label: _cls.label },
            classificationValue: {
                value: _cls.classificationValues[0],
                label: t(_cls.classificationValues[0]) + " (" + _cls.classificationValues[0] + ")",
            },
            classificationValues: opts,
            amount: parseFloat(el.priceWithoutVat).toFixed(2),
        }];
    }

    return [];
}

function buildLoadedMoreTaxes(el, myDataMoreTaxesOptions, t) {
    if (!el.moreTaxes) return [];

    const groupTaxes = myDataMoreTaxesOptions.reduce((r, a) => {
        r[a.type] = [...(r[a.type] || []), a];
        return r;
    }, {});

    return el.moreTaxes.map(m => {
        if (m.deductionName && m.deductionAmountValue) {
            m.deduction = { id: m.id, amountValue: m.deductionAmountValue, name: m.deductionName };
        }

        let _value;
        if (m.deduction) {
            _value = m.deduction.id;
            m.myDataMoreTax = { ...m.deduction, type: "DEDUCTION" };
        } else {
            _value = "TAX_" + m.myDataMoreTax.uid;
        }

        const type = m.myDataMoreTax.type;
        const typeTaxes = groupTaxes[type] || [];

        return {
            amount: m.amount,
            myDataMoreTax: {
                label: m.deduction ? m.deduction.name : t(_value),
                value: _value,
                amountValue: m.myDataMoreTax.amountValue,
                uid: m.myDataMoreTax.uid,
                key: m.myDataMoreTax.key,
                type,
                subtracted: m.deduction ? m.deduction.subtracted : null,
            },
            type: {
                label: t(type),
                value: type,
                amountValue: m.myDataMoreTax.amountValue,
            },
            myDataMoreTaxes: typeTaxes,
        };
    });
}
